//---------------------------------------------------------------------
// React, MUI, and other third-party modules imports
//---------------------------------------------------------------------
import React, { useContext, useState, useEffect, useCallback } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { BrowserRouter as Router, Route, Switch } from 'react-router-dom';

//---------------------------------------------------------------------
// Common helpers and constants imports
//---------------------------------------------------------------------
import {
    PATH_ACCOUNT_SETUP,
    PATH_ADVERTISE,
    PATH_ADMIN_CONSOLE,
    PATH_SURVEY,
    PATH_AUTH,
    PATH_AUTH_HANDLER,
    PATH_BRAND,
    PATH_DASHBOARD,
    PATH_EMP_ACCOUNT_LINKAGE,
    PATH_EMP_INVITE_HANDLER,
    PATH_EMP_SEARCH,
    PATH_HOME,
    PATH_JOB_CENTER,
    PATH_RESOURCE_CENTER,
    PATH_TALENT_CENTER,
    PATH_TALENT,
    PATH_SUB_PAGE_JOB,
    PATH_SUB_PAGE_EMP,
    PATH_SUB_PAGE_JS,
    COMPOSITE_DOC_ID_DELIMITER,
    PATH_JOIN_INTERVIEW,
    PATH_EMP_MEMBER_SETUP,
    PATH_FORGOT_PASSWORD,
    PATH_LOGIN,
    PATH_SIGN_IN,
    SUMMER_INTERN_PROGRAM,
    PATH_ADMIN_UTIL,
} from './system/constants';
import { unsubscribeRealtimeUpdates } from './system/firebase';
import { AppContext, LayoutContext } from './common/contexts';
import {
    ACCOUNT_STATUS_ACTIVE,
    ACCOUNT_STATUS_PENDING_ACCOUNT_SETUP,
    ACCOUNT_STATUS_PENDING_MEMBER_SETUP,
    ACCOUNT_STATUS_PENDING_EMAIL_VERIFICATION,
    ACCOUNT_STATUS_INACTIVE,
    BASE_URL,
    DASHBOARD_ID_MY_JOBS,
    USER_TYPE_EMP,
    ROUTE_TYPE_EMP,
    ROUTE_TYPE_JS,
    USER_TYPE_JS,
    ROUTE_TYPE_JOB_CENTER,
    ROUTE_TYPE_RESOURCE_CENTER,
    ROUTE_TYPE_LOGIN,
    ROUTE_TYPE_CREATE_ACCOUNT_JS,
    ROUTE_TYPE_CREATE_ACCOUNT_EMP,
    ROUTE_TYPE_CREATE_ACCOUNT,
    HOMEPAGE_CONTACT,
    HOMEPAGE_ABOUT,
    HOMEPAGE_EMPLOYERS,
    HOMEPAGE_FAQ,
} from './common/constants';
import { loadUserData, loadEmployerData, loadLovs } from './common/services/appServices';
import { handleCloseSnackbar } from './common/snackbarHelper';
import useMessageDialog from './common/hooks/useMessageDialog';
import { loadSimpleLovs } from './common/services/lovServices';
import { loadDashboardById } from './common/services/dashboardServices';
import { activateUserAccountAfterVerifiedEmail } from './common/services/authServices';
import { useWindowSize } from './common/scrollHelper';

//---------------------------------------------------------------------
// Common components imports
//---------------------------------------------------------------------
import PrivateRoute from './common/elemental/AfitdPrivateRoute';
import AfitdSnackbar from './common/elemental/AfitdSnackbar';
import AfitdLoadingDialog from './common/elemental/AfitdLoadingDialog';
import PrivateRouteSuperUser from './common/elemental/AfitdPrivateRouteSuperUser';
import PublicRoute from './common/elemental/AfitdPublicRoute';
import AfitdLoginRoute from './common/elemental/AfitdLoginRoute';

//---------------------------------------------------------------------
// Page specific components imports
//---------------------------------------------------------------------
import UserAlerts from './UserAlerts';
import HomeLayout from './home/HomeLayout';
import AboutUsLayout from './home/AboutUsLayout';
import ContactUsLayout from './home/ContactUsLayout';
import EmployersLayout from './home/EmployersLayout';
import FaqLayout from './home/FaqLayout';
import DashboardLayout from './dashboard/DashboardLayout';
import EmpDashboardLayout from './empDashboard/DashboardLayout';
import JobCenterLayout from './jobCenter/JobCenterLayout';
import ResourceCenterLayout from './resourceCenter/ResourceCenterLayout';
import ResourceCenterArticleLayout from './resourceCenter/ResourceCenterArticleLayout';
import TalentCenterLayout from './empTalentCenter/TalentCenterLayout';
import EmpBrandPageLayout from './empBrandPage/EmpBrandPageLayout';
import JobSeekerProfileLayout from './jobSeekerProfile/JobSeekerProfileLayout';
import EmpSearchLayout from './empSearch/EmpSearchLayout';
import AdvertiseLayout from './advertise/AdvertiseLayout';
import AuthHandlerLayout from './authHandler/AuthHandlerLayout';
import AuthLayout from './authHandler/AuthLayout';
import EmpAccountSetupLayout from './empDashboard/AccountSetupLayout';
import EmpMemberSetupLayout from './empDashboard/MemberSetupLayout';
import TempAuthLayout from './tempAuthHandler/TempAuthLayout';
import EmpInviteHandlerLayout from './empInviteHandler/EmpInviteHandlerLayout';
import JoinInterviewLayout from './interview/JoinInterviewLayout';
import EmpAccountLinkageLayout from './empAccountLinkage/EmpAccountLinkageLayout';
import AdminConsoleLayout from './AdminConsole2/AdminConsoleLayout';
import SurveyLayout from './Survey2/SurveyLayout';
import ForgotPasswordLayout from './auth/ForgotPasswordLayout';
import QuickAdmin from './admin/QuickAdmin';
import BlockJsLayout from './auth/BlockJsLayout';
import LaunchScreen from './LaunchScreen';
import ThankYouForPurchaseDialog from './empDashboardMySettings/subComponents/ThankYouForPurchaseDialog';
import PageMessage from './common/elemental/PageMessage';
import GenericLayout from './common/elemental/GenericLayout';
import SummerInternProgramLayout from './summerInternProgram/SummerInternProgramLayout';
import AdminUtilLayout from './AdminUtil/AdminUtilLayout';

//---------------------------------------------------------------------
// Utilities (clsx, lodash, date-fns, etc.) imports
//---------------------------------------------------------------------
import _get from 'lodash/get';
import _findIndex from 'lodash/findIndex';
import _isNil from 'lodash/isNil';
import _noop from 'lodash/noop';

//---------------------------------------------------------------------
// CSS
//---------------------------------------------------------------------
const useStyles = (params = {}) => {
    const { windowInnerHeight } = params;

    return makeStyles(theme => ({
        root: {
            backgroundColor: '#FAFAFC',
            height: windowInnerHeight ? `${windowInnerHeight}px` : '100%', //'100%',
        },
    }));
};

// const styles = theme => {
//     const windowInnerHeight = window.innerHeight;
//     return {
//         root: {
//             backgroundColor: '#FAFAFC',
//             height: windowInnerHeight ? `${windowInnerHeight}px` : '100%', //'100%',
//         },
//     };
// };

const NotFound = props => {
    return (
        <GenericLayout>
            <PageMessage pageNotFound />
        </GenericLayout>
    );
};

const getPageComponent = ({ auth, isLoading, isAuthenticationPending, user } = {}) => {
    // if done authenticating and 'currentUser' exists (user is logged in) but 'user' is NIL, it is loading the user data
    const isLoadingUser = !isAuthenticationPending && auth.currentUser && !user;

    const userType = _get(user, 'type');
    const isEmp = userType === USER_TYPE_EMP;
    const isJs = userType === USER_TYPE_JS;

    return isAuthenticationPending || isLoadingUser || isLoading ? (
        <LaunchScreen />
    ) : (
        <Router>
            <Switch>
                {/* <PrivateRoute exact path={`${BASE_URL}/`}>
                    {isEmp && <EmpDashboardLayout />}
                    {!isEmp && <DashboardLayout />}
                </PrivateRoute> */}
                <Route exact path={`${BASE_URL}${PATH_AUTH_HANDLER}`}>
                    <AuthHandlerLayout />
                </Route>
                <Route exact path={`${BASE_URL}${PATH_AUTH}`}>
                    <AuthLayout />
                </Route>
                <PublicRoute exact path={`${BASE_URL}${PATH_EMP_INVITE_HANDLER}/:code`}>
                    <EmpInviteHandlerLayout />
                </PublicRoute>
                <PublicRoute exact path={`${BASE_URL}${PATH_JOIN_INTERVIEW}/:code/:interviewerId`}>
                    <JoinInterviewLayout />
                </PublicRoute>
                <PublicRoute exact path={`${BASE_URL}${PATH_JOIN_INTERVIEW}/req/j/:code`}>
                    <JoinInterviewLayout isJs />
                </PublicRoute>
                <PublicRoute exact path={`${BASE_URL}${PATH_JOIN_INTERVIEW}/req/e/:code`}>
                    <JoinInterviewLayout isEmp />
                </PublicRoute>
                <PublicRoute exact path={`${BASE_URL}${PATH_ACCOUNT_SETUP}`}>
                    {isEmp && <EmpAccountSetupLayout />}
                    {isJs && !isEmp && <DashboardLayout />}
                </PublicRoute>
                <PublicRoute exact path={`${BASE_URL}/`}>
                    <HomeLayout />
                </PublicRoute>
                <PublicRoute exact path={`${BASE_URL}${PATH_HOME}`}>
                    <HomeLayout />
                </PublicRoute>
                <PublicRoute exact path={`${BASE_URL}${PATH_HOME}/${HOMEPAGE_ABOUT}`}>
                    <AboutUsLayout />
                </PublicRoute>
                <PublicRoute exact path={`${BASE_URL}${PATH_HOME}/${HOMEPAGE_CONTACT}`}>
                    <ContactUsLayout />
                </PublicRoute>
                <PublicRoute exact path={`${BASE_URL}${PATH_HOME}/${HOMEPAGE_EMPLOYERS}`}>
                    <EmployersLayout />
                </PublicRoute>
                <PublicRoute exact path={`${BASE_URL}${PATH_HOME}/${HOMEPAGE_FAQ}`}>
                    <FaqLayout />
                </PublicRoute>
                <PublicRoute exact path={`${BASE_URL}${PATH_HOME}/p/:profileId`}>
                    <HomeLayout />
                </PublicRoute>
                <PublicRoute exact path={`${BASE_URL}${PATH_HOME}/${HOMEPAGE_FAQ}/:subPageId`}>
                    <FaqLayout />
                </PublicRoute>
                <AfitdLoginRoute exact path={`${BASE_URL}${PATH_LOGIN}`} type={ROUTE_TYPE_LOGIN}>
                    <HomeLayout />
                </AfitdLoginRoute>
                <AfitdLoginRoute exact path={`${BASE_URL}${PATH_SIGN_IN}`} type={ROUTE_TYPE_CREATE_ACCOUNT}>
                    <HomeLayout />
                </AfitdLoginRoute>
                <AfitdLoginRoute exact path={`${BASE_URL}${PATH_SIGN_IN}/j`} type={ROUTE_TYPE_CREATE_ACCOUNT_JS}>
                    <HomeLayout />
                </AfitdLoginRoute>
                <AfitdLoginRoute exact path={`${BASE_URL}${PATH_SIGN_IN}/e`} type={ROUTE_TYPE_CREATE_ACCOUNT_EMP}>
                    <HomeLayout />
                </AfitdLoginRoute>
                <Route exact path={`${BASE_URL}${PATH_FORGOT_PASSWORD}/`}>
                    <ForgotPasswordLayout />
                </Route>
                <PrivateRoute exact type={ROUTE_TYPE_EMP} path={`${BASE_URL}${PATH_EMP_ACCOUNT_LINKAGE}`}>
                    <EmpAccountLinkageLayout />
                </PrivateRoute>
                <PrivateRoute exact path={`${BASE_URL}${PATH_EMP_MEMBER_SETUP}`}>
                    {isEmp && <EmpMemberSetupLayout />}
                    {isJs && !isEmp && <DashboardLayout />}
                </PrivateRoute>
                <PrivateRoute exact type={ROUTE_TYPE_EMP} path={`${BASE_URL}${PATH_TALENT_CENTER}`}>
                    <TalentCenterLayout />
                </PrivateRoute>
                <PrivateRoute exact type={ROUTE_TYPE_EMP} path={`${BASE_URL}${PATH_TALENT_CENTER}/:pageNumber`}>
                    <TalentCenterLayout />
                </PrivateRoute>
                <PublicRoute exact type={ROUTE_TYPE_JOB_CENTER} path={`${BASE_URL}${PATH_JOB_CENTER}`}>
                    <JobCenterLayout />
                </PublicRoute>
                <PublicRoute exact type={ROUTE_TYPE_JS} path={`${BASE_URL}${PATH_JOB_CENTER}/:pageNumber`}>
                    <JobCenterLayout />
                </PublicRoute>
                <PublicRoute exact type={ROUTE_TYPE_JS} path={`${BASE_URL}${PATH_JOB_CENTER}/${PATH_SUB_PAGE_EMP}/:employerId`}>
                    <JobCenterLayout />
                </PublicRoute>
                <PublicRoute exact type={ROUTE_TYPE_JOB_CENTER} path={`${BASE_URL}${PATH_JOB_CENTER}/${PATH_SUB_PAGE_JOB}/:jobId`}>
                    <JobCenterLayout />
                </PublicRoute>
                <PublicRoute exact type={ROUTE_TYPE_RESOURCE_CENTER} path={`${BASE_URL}${PATH_RESOURCE_CENTER}`}>
                    <ResourceCenterLayout />
                </PublicRoute>
                <PublicRoute exact type={ROUTE_TYPE_RESOURCE_CENTER} path={`${BASE_URL}${PATH_RESOURCE_CENTER}/:articleId`}>
                    <ResourceCenterArticleLayout />
                </PublicRoute>
                <PrivateRoute exact type={ROUTE_TYPE_JS} path={`${BASE_URL}${PATH_EMP_SEARCH}`}>
                    <EmpSearchLayout />
                </PrivateRoute>
                <PrivateRoute exact type={ROUTE_TYPE_JS} path={`${BASE_URL}${PATH_EMP_SEARCH}/:pageNumber`}>
                    <EmpSearchLayout />
                </PrivateRoute>
                <PublicRoute exact path={`${BASE_URL}${PATH_ADVERTISE}/:pageId`}>
                    {isEmp && !isJs && <AdvertiseLayout />}
                    {isJs && <BlockJsLayout />}
                </PublicRoute>
                <PrivateRouteSuperUser exact type={ROUTE_TYPE_EMP} path={`${BASE_URL}${PATH_ADMIN_CONSOLE}/:pageId`}>
                    <AdminConsoleLayout />
                </PrivateRouteSuperUser>
                <PrivateRouteSuperUser exact type={ROUTE_TYPE_EMP} path={`${BASE_URL}${PATH_ADMIN_CONSOLE}/:pageId/details/:subPageId`}>
                    <AdminConsoleLayout />
                </PrivateRouteSuperUser>
                \{' '}
                <PrivateRouteSuperUser exact type={ROUTE_TYPE_EMP} path={`${BASE_URL}${PATH_ADMIN_UTIL}/:pageId`}>
                    <AdminUtilLayout />
                </PrivateRouteSuperUser>
                <PublicRoute exact path={`${BASE_URL}${PATH_SURVEY}/:surveyDocId`}>
                    <SurveyLayout />
                </PublicRoute>
                <PrivateRoute exact path={`${BASE_URL}${PATH_DASHBOARD}`}>
                    {isEmp && <EmpDashboardLayout />}
                    {isJs && !isEmp && <DashboardLayout />}
                </PrivateRoute>
                <PrivateRoute exact path={`${BASE_URL}${PATH_DASHBOARD}/:pageId`}>
                    {isEmp && <EmpDashboardLayout />}
                    {isJs && !isEmp && <DashboardLayout />}
                </PrivateRoute>
                <PrivateRoute exact path={`${BASE_URL}${PATH_DASHBOARD}/:pageId/addJob`}>
                    {isEmp && <EmpDashboardLayout />}
                    {isJs && !isEmp && <DashboardLayout />}
                </PrivateRoute>
                <PrivateRoute exact path={`${BASE_URL}${PATH_DASHBOARD}/:pageId/:subPageId`}>
                    {isEmp && <EmpDashboardLayout />}
                    {isJs && !isEmp && <DashboardLayout />}
                </PrivateRoute>
                <PrivateRoute exact path={`${BASE_URL}${PATH_DASHBOARD}/:pageId/:subPageId/p/:pageNumber`}>
                    {isEmp && <EmpDashboardLayout />}
                    {isJs && !isEmp && <DashboardLayout />}
                </PrivateRoute>
                <PrivateRoute exact path={`${BASE_URL}${PATH_DASHBOARD}/${DASHBOARD_ID_MY_JOBS}/${PATH_SUB_PAGE_JOB}/:jobId`}>
                    {isEmp && <EmpDashboardLayout />}
                    {isJs && !isEmp && <div>Employer Only</div>}
                </PrivateRoute>
                <PublicRoute exact path={`${BASE_URL}${PATH_BRAND}/${PATH_SUB_PAGE_EMP}/:brandEmployerId`}>
                    <EmpBrandPageLayout />
                </PublicRoute>
                <PublicRoute exact path={`${BASE_URL}${PATH_BRAND}/${PATH_SUB_PAGE_EMP}/:brandEmployerId/:tabId`}>
                    <EmpBrandPageLayout />
                </PublicRoute>
                <PublicRoute exact path={`${BASE_URL}${PATH_BRAND}/:brandCustomName`}>
                    <EmpBrandPageLayout />
                </PublicRoute>
                <PublicRoute exact path={`${BASE_URL}${PATH_TALENT}/${PATH_SUB_PAGE_JS}/:talentUserId`}>
                    <JobSeekerProfileLayout />
                </PublicRoute>
                <PublicRoute exact path={`${BASE_URL}${PATH_TALENT}/:talentUsername`}>
                    <JobSeekerProfileLayout />
                </PublicRoute>
                <PrivateRoute exact path={`${BASE_URL}/tempAuthHandler/:subPageId`}>
                    <TempAuthLayout />
                </PrivateRoute>
                <PrivateRoute exact path={`${BASE_URL}/tempAuthHandler/:subPageId/:pageNumber`}>
                    <TempAuthLayout />
                </PrivateRoute>
                <PublicRoute exact path={`${BASE_URL}${SUMMER_INTERN_PROGRAM}`}>
                    <SummerInternProgramLayout />
                </PublicRoute>
                <PrivateRouteSuperUser exact path={`${BASE_URL}/quick-admin/owSoenQPvcne888`}>
                    <QuickAdmin />
                </PrivateRouteSuperUser>
                <Route path="*" component={NotFound} />
            </Switch>
        </Router>
    );
};

//---------------------------------------------------------------------
// Component
//---------------------------------------------------------------------
const PageBase = props => {
    //---------------------------------------------------------------------
    // From context and other hooks
    //---------------------------------------------------------------------
    const appContext = useContext(AppContext);
    const { auth, authUser, isAuthenticationPending, sys, lovs, setLovs } = appContext;

    const { PendingAccountSetupMessageDialog, setOpen: setPendingAccountSetupMessageDialogOpen } = useMessageDialog();
    const { DeleteAccountSuccessfulMessageDialog, setOpen: setDeleteAccountSuccessfulMessageDialogOpen } = useMessageDialog();

    const windowSize = useWindowSize();

    //---------------------------------------------------------------------
    // From props
    //---------------------------------------------------------------------
    // const { classes } = props;

    //---------------------------------------------------------------------
    // Local states
    //---------------------------------------------------------------------
    const [isLoading, setIsLoading] = useState(true);
    const [user, setUser] = useState(null);
    const [employer, setEmployer] = useState(null);
    const [currentEmployerIdx, setCurrentEmployerIdx] = useState();
    const [dashboardData, setDashboardData] = useState();
    const [dashboardDataEmpMember, setDashboardDataEmpMember] = useState();
    // const [esToken, setEsToken] = useState();
    const [thankYouForPurchaseDialogOpen, setThankYouForPurchaseDialogOpen] = useState(false);
    const [isPageDirty, setIsPageDirty] = useState(false);
    const [userDocExist, setUserDocExist] = useState(false);

    const [tempBookmarkedJobs, setTempBookmarkedJobs] = React.useState({});
    const [tempUnbookmarkedJobs, setTempUnbookmarkedJobs] = React.useState({});
    const [tempAppliedJobs, setTempAppliedJobs] = React.useState({});

    const [loadingDialogOpen, setLoadingDialogOpen] = useState();
    const [snackbarMessage, setSnackbarMessage] = useState();
    const [snackbarErrorMessage, setSnackbarErrorMessage] = useState();

    //---------------------------------------------------------------------
    // Derived
    //---------------------------------------------------------------------
    const userId = authUser?.uid;
    const emailVerified = _get(authUser, 'emailVerified');
    const authTypeEmail = _get(user, 'authTypeEmail');
    const userType = _get(user, 'type');
    const isEmp = userType === USER_TYPE_EMP;
    const isJs = userType === USER_TYPE_JS;
    const accountStatus = isEmp ? _get(user, `employers[${currentEmployerIdx}].status`) : _get(user, 'status');
    const pendingAccountSetup = accountStatus === ACCOUNT_STATUS_PENDING_ACCOUNT_SETUP;
    const pendingMemberSetup = accountStatus === ACCOUNT_STATUS_PENDING_MEMBER_SETUP;
    const pendingEmpAccountLinkage = _get(user, 'isInviteMode', false) && _get(user, 'openInviteCount', 0) > 0;

    // get the current employer Id or the first employer Id (if exists)
    const userEmployerList = _get(user, 'employers', []);
    const employerId = _get(user, `employers[${currentEmployerIdx}].employerId`, _get(user, 'employers[0].employerId'));

    // get the status of emp user's own company (first account)
    const firstEmpAccountStatus = _get(userEmployerList, '[0].status');

    const dashboardDocId = isEmp ? employerId : userId;
    const empMemberUserId = isEmp ? userId : null;

    //---------------------------------------------------------------------
    // Side effects
    //---------------------------------------------------------------------
    // Load/clear user and system data based on user authentication
    useEffect(() => {
        const loadIndustryLov = async () => {
            const industryLov = await loadSimpleLovs({ lovName: 'industries' });
            const occupationLov = await loadSimpleLovs({ lovName: 'occupations' });
            const otherEducationLov = await loadSimpleLovs({ lovName: 'otherEducations' });
            const skillsetLov = await loadSimpleLovs({ lovName: 'skillSets' });

            setLovs(lovs => {
                return { ...lovs, industryLov, occupationLov, otherEducationLov, skillsetLov };
            });
        };
        if (authUser?.uid) {
            const unsubscribeLoadUserData = loadUserData({
                userId: authUser.uid,
                setUser,
                callback: async ({ userDocExist } = {}) => {
                    setUserDocExist(userDocExist);
                    // setCurrentEmployerIdx(null);
                    loadIndustryLov();
                    setIsLoading(false);
                },
            });
            const unsubscribeLoadLovs = loadLovs(setLovs);

            // unsubscribe realtime update on unmount
            return () => {
                unsubscribeRealtimeUpdates([unsubscribeLoadUserData, unsubscribeLoadLovs]);
            };
        } else {
            setUser(null);
            setEmployer(null);
            setCurrentEmployerIdx(null);
            setIsLoading(false);
        }
    }, [authUser, setLovs]);

    // User verified email, update account status
    useEffect(() => {
        if (
            (emailVerified || !authTypeEmail) &&
            ((isEmp && firstEmpAccountStatus === ACCOUNT_STATUS_PENDING_EMAIL_VERIFICATION) || (isJs && accountStatus === ACCOUNT_STATUS_PENDING_EMAIL_VERIFICATION))
        ) {
            activateUserAccountAfterVerifiedEmail({ userId });
        }
    }, [emailVerified, authTypeEmail, isEmp, isJs, accountStatus, firstEmpAccountStatus, userId]);

    // Initialize/update currentEmployerIdx
    useEffect(() => {
        if (userEmployerList.length > 0 && _isNil(currentEmployerIdx)) {
            // look for the first active employer
            let newCurrentEmployerIdx = _findIndex(userEmployerList, o => o.status === ACCOUNT_STATUS_ACTIVE);

            // if no active employer, look for the first PMS (pending member setup) employer
            if (newCurrentEmployerIdx < 0) newCurrentEmployerIdx = _findIndex(userEmployerList, o => o.status === ACCOUNT_STATUS_PENDING_MEMBER_SETUP);

            // if no PMS employer, look for the first employer that is not inactive (this should be rare)
            if (newCurrentEmployerIdx < 0) newCurrentEmployerIdx = _findIndex(userEmployerList, o => o.status !== ACCOUNT_STATUS_INACTIVE);

            // if no active or PMS employer or non-inactive, go to the first employer on the list
            setCurrentEmployerIdx(newCurrentEmployerIdx < 0 ? 0 : newCurrentEmployerIdx);
        }
    }, [userEmployerList, currentEmployerIdx]);

    // load employer data
    useEffect(() => {
        if (employerId) {
            const unsubscribeLoadEmployerData = loadEmployerData({ setEmployer, employerId });

            // unsubscribe realtime update on unmount
            return () => {
                unsubscribeRealtimeUpdates(unsubscribeLoadEmployerData);
            };
        } else {
            setEmployer(null);
        }
    }, [employerId]);

    // listener for user's or employer's dashboard
    useEffect(() => {
        let unsubscribeLoadDashboardByUserId = null;
        if (!_isNil(userType) && dashboardDocId) {
            unsubscribeLoadDashboardByUserId = loadDashboardById({ docId: dashboardDocId, setData: setDashboardData });
        }

        // unsubscribe realtime update on unmount
        return () => {
            unsubscribeRealtimeUpdates([unsubscribeLoadDashboardByUserId]);
        };
    }, [userType, dashboardDocId]);

    // listener for employer member's dashboard
    useEffect(() => {
        let unsubscribeLoadDashboardByUserId = null;
        if (!_isNil(userType) && employerId && empMemberUserId) {
            unsubscribeLoadDashboardByUserId = loadDashboardById({ docId: `${empMemberUserId}${COMPOSITE_DOC_ID_DELIMITER}${employerId}`, setData: setDashboardDataEmpMember });
        }

        // unsubscribe realtime update on unmount
        return () => {
            unsubscribeRealtimeUpdates([unsubscribeLoadDashboardByUserId]);
        };
    }, [userType, employerId, empMemberUserId]);

    //---------------------------------------------------------------------
    // useCallback / Event handlers
    //---------------------------------------------------------------------
    const handleCloseLoadingDialog = useCallback(() => {
        setLoadingDialogOpen(false);
    }, [setLoadingDialogOpen]);

    const handleCloseThankYouForPurchaseDialog = () => {
        setThankYouForPurchaseDialogOpen(false);
    };

    //---------------------------------------------------------------------
    // Defns
    //---------------------------------------------------------------------
    const classes = useStyles({ windowInnerHeight: windowSize?.height })();

    //---------------------------------------------------------------------
    // Render
    //---------------------------------------------------------------------
    return (
        <AppContext.Provider
            value={{
                ...appContext,
                sys,
                currentUser: user, //{ ...user }, //RESEARCH Why setting a new instance each time?
                setCurrentUser: setUser,
                userId,
                userDocExist,
                isEmp,
                currentEmployer: employer,
                employerId,
                currentEmployerIdx,
                setCurrentEmployerIdx,
                lovs,
                emailVerified,
                authTypeEmail,
                pendingAccountSetup,
                pendingEmpAccountLinkage,
                pendingMemberSetup,
                setPendingAccountSetupMessageDialogOpen,
                setDeleteAccountSuccessfulMessageDialogOpen,
                thankYouForPurchaseDialogOpen,
                setThankYouForPurchaseDialogOpen,
                isPageDirty,
                setIsPageDirty,
                esToken: null,
                setEsToken: _noop,
            }}
        >
            <LayoutContext.Provider
                value={{
                    userId,
                    setLoadingDialogOpen,
                    dashboardData,
                    setDashboardData,
                    dashboardDataEmpMember,
                    setDashboardDataEmpMember,
                    tempBookmarkedJobs,
                    setTempBookmarkedJobs,
                    tempUnbookmarkedJobs,
                    setTempUnbookmarkedJobs,
                    tempAppliedJobs,
                    setTempAppliedJobs,
                    setSnackbarMessage,
                    setSnackbarErrorMessage,
                }}
            >
                <div className={classes.root}>
                    {getPageComponent({ auth, isLoading, isAuthenticationPending, user })}
                    <UserAlerts />
                </div>
                <AfitdSnackbar
                    message={{ successMessage: snackbarMessage, errorMessage: snackbarErrorMessage }}
                    onClose={() => handleCloseSnackbar(setSnackbarMessage, setSnackbarErrorMessage)}
                />
                <AfitdLoadingDialog open={loadingDialogOpen} onClose={handleCloseLoadingDialog} />
                <PendingAccountSetupMessageDialog />
                <DeleteAccountSuccessfulMessageDialog />
                <ThankYouForPurchaseDialog open={thankYouForPurchaseDialogOpen} onClose={handleCloseThankYouForPurchaseDialog} />
            </LayoutContext.Provider>
        </AppContext.Provider>
    );
};

// export default withStyles(styles)(PageBase);
export default PageBase;
